import React from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaArrowRight, FaTrophy, FaUsers, FaMedal, FaHeart } from 'react-icons/fa';
import './About.css';

const About = () => {
  const values = [
    {
      icon: <FaTrophy />,
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, from coaching to facility maintenance.'
    },
    {
      icon: <FaUsers />,
      title: 'Community',
      description: 'We foster a welcoming community where players of all levels can thrive and grow.'
    },
    {
      icon: <FaMedal />,
      title: 'Integrity',
      description: 'We uphold the highest standards of sportsmanship and ethical conduct.'
    },
    {
      icon: <FaHeart />,
      title: 'Passion',
      description: 'Our love for badminton drives us to share this wonderful sport with everyone.'
    }
  ];

  const milestones = [
    { year: '2009', title: 'Club Founded', description: 'Net Nest opens its doors with 4 courts' },
    { year: '2012', title: 'First Tournament', description: 'Hosted our first regional tournament' },
    { year: '2015', title: 'Expansion', description: 'Expanded to 8 professional courts' },
    { year: '2018', title: 'Academy Launch', description: 'Launched junior development academy' },
    { year: '2020', title: 'Digital Era', description: 'Introduced online booking system' },
    { year: '2023', title: 'Full Renovation', description: 'State-of-the-art facility with 12 courts' }
  ];

  return (
    <div className="about-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="page-header-overlay"></div>
        <div className="container">
          <div className="page-header-content">
            <span className="page-badge">About Us</span>
            <h1>Our Story & Legacy</h1>
            <p>Discover the passion, dedication, and community that make Net Nest special</p>
            <nav className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>About Us</span>
            </nav>
          </div>
        </div>
      </section>

      {/* Main About Section */}
      <section className="about-main section">
        <div className="container">
          <div className="about-main-grid">
            <div className="about-main-content">
              <span className="section-badge">Who We Are</span>
              <h2>Premier Badminton Excellence Since 2009</h2>
              <p>
                Net Nest Badminton Sports Club is a dedicated badminton training and development center committed to excellence in sports, fitness, and personal growth. Established with the vision of promoting badminton at all levels, our club provides a professional and structured environment where players can learn, train, and excel with confidence.
              </p>
              <p>
                At Net Nest, we believe badminton is more than just a sport—it is a pathway to discipline, resilience, teamwork, and a healthy lifestyle. Our club welcomes players of all age groups and skill levels, from beginners discovering the basics to advanced athletes preparing for competitive tournaments.
              </p>
              <div className="about-features">
                <div className="feature-item">
                  <FaCheckCircle />
                  <span>12 Professional Courts</span>
                </div>
                <div className="feature-item">
                  <FaCheckCircle />
                  <span>Expert Certified Coaches</span>
                </div>
                <div className="feature-item">
                  <FaCheckCircle />
                  <span>5000+ Active Members</span>
                </div>
                <div className="feature-item">
                  <FaCheckCircle />
                  <span>15+ Years Experience</span>
                </div>
              </div>
            </div>
            <div className="about-main-images">
              <img src="https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=600" alt="Badminton Court" className="main-image" />
              <img src="https://images.unsplash.com/photo-1599474924187-334a4ae5bd3c?w=400" alt="Training" className="secondary-image" />
            </div>
          </div>
          <div className="about-main-grid">
            <div className="about-main-content">
              <h2>Our Philosophy</h2>
              <p>
                Our philosophy is built on the principles of quality training, consistency, and holistic development. We focus not only on technical skills but also on physical fitness, mental strength, and sportsmanship. Every player at Net Nest is trained to perform with confidence, respect the game, and continuously strive for improvement.
              </p>
            </div>
            <div className="about-main-images">
              <h2>Professional Coaching & Training Approach</h2>
              <p>
                Strong fundamentals and correct techniques
                <br></br>
                Footwork, agility, and court movement
                <br></br>
                Tactical understanding of singles and doubles play
                <br></br>
                Mental conditioning and match temperament
                <br></br>
                Injury prevention and long-term fitness
              </p>
            </div>
            <div className="about-main-images">
              <h2>Infrastructure & Training Environment</h2>
              <p>
                International-standard indoor badminton courts
                <br></br>
                High-quality flooring designed to reduce injury risk
                <br></br>
                Proper lighting for professional-level play
                <br></br>
                Clean, well-maintained facilities
              </p>
            </div>
            <div className="about-main-images">
              <h2>Commitment to Player Development</h2>
              <p>
                Technical excellence
                <br></br>
                Physical endurance and agility
                <br></br>
                Confidence and competitive mindset
                <br></br>
                Discipline and time management
              </p>
            </div>
            <div className="about-main-images">
              <h2>Our Community</h2>
              <p>
                Net Nest Badminton Sports Club is more than a training center—it is a community of passionate players, supportive coaches, and motivated families. We encourage a positive and inclusive atmosphere where players learn to grow together, support each other, and celebrate progress and achievements.
              </p>
            </div>
            <div className="about-main-images">
              <h2>Our Goal</h2>
              <p>
                Our goal is to create a strong foundation for badminton excellence while inspiring players to adopt a lifelong love for the sport. Through quality coaching, structured programs, and a value-driven approach, Net Nest Badminton Sports Club continues to shape confident players and responsible individuals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section section">
        <div className="container">
          <div className="section-header center">
            <span className="section-badge">Our Values</span>
            <h2>What Drives Us Forward</h2>
            <p>The core principles that guide everything we do at Net Nest</p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div className="value-card" key={index}>
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="about-links section">
        <div className="container">
          <div className="links-grid">
            <Link to="/history" className="link-card">
              <h3>Our History</h3>
              <p>Explore the complete story of Net Nest from 2009 to present</p>
              <span className="link-arrow"><FaArrowRight /></span>
            </Link>
            <Link to="/mission-vision" className="link-card">
              <h3>Mission & Vision</h3>
              <p>Discover our goals and aspirations for the future of badminton</p>
              <span className="link-arrow"><FaArrowRight /></span>
            </Link>
            <Link to="/why-choose-us" className="link-card">
              <h3>Why Choose Us</h3>
              <p>Learn what sets Net Nest apart from other badminton facilities</p>
              <span className="link-arrow"><FaArrowRight /></span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
